import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import FunnelLayout from "@/v2/components/FunnelLayout";
import GlassCard from "@/v2/components/GlassCard";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";

type Status = "pending" | "ready" | "failed" | "timeout";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 30;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState<Status>("pending");
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (authLoading) return;
    if (!sessionId) {
      navigate("/v2/pricing", { replace: true });
      return;
    }
    if (!user) {
      navigate(
        `/login?redirect=${encodeURIComponent(`/v2/checkout/success?session_id=${sessionId}`)}`,
        { replace: true },
      );
      return;
    }

    let cancelled = false;
    let timer: number | undefined;

    async function poll() {
      attemptsRef.current += 1;
      try {
        const { data, error } = await supabase.functions.invoke(
          `v2-checkout-status?session_id=${encodeURIComponent(sessionId!)}&environment=${getStripeEnvironment()}`,
          { method: "GET" },
        );
        if (cancelled) return;
        if (error) throw error;
        if (data?.status === "ready") {
          setStatus("ready");
          return;
        }
        if (data?.status === "failed") {
          setStatus("failed");
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        timer = window.setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [authLoading, user, sessionId, navigate]);

  return (
    <FunnelLayout>
      <div className="max-w-xl mx-auto space-y-6 text-center">
        <p className="text-xs uppercase tracking-widest text-secondary">Conferma</p>
        <h1 className="text-3xl md:text-4xl font-bold">
          {status === "ready" ? (
            <>
              Sprint <span className="text-primary">attivato</span>
            </>
          ) : status === "failed" || status === "timeout" ? (
            <>Pagamento non confermato</>
          ) : (
            <>Stiamo confermando il pagamento…</>
          )}
        </h1>

        <GlassCard className="text-left">
          {status === "pending" && (
            <div className="flex items-center gap-3 text-sm text-slate-300">
              <span className="inline-block h-3 w-3 rounded-full bg-secondary animate-pulse" />
              <span>Attendere qualche secondo. Stiamo registrando il tuo Sprint.</span>
            </div>
          )}
          {status === "ready" && (
            <div className="space-y-5 text-left">
              <p className="text-sm text-slate-300">
                Il tuo Sprint è attivo. Nei prossimi 14 giorni seguirai 5 step per trasformare la tua idea in evidenze reali.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2"><span className="text-secondary font-bold shrink-0">1.</span> Scegli l'ipotesi da testare</li>
                <li className="flex items-start gap-2"><span className="text-secondary font-bold shrink-0">2.</span> Definisci il protocollo del test</li>
                <li className="flex items-start gap-2"><span className="text-secondary font-bold shrink-0">3.</span> Esegui e raccogli contatti</li>
                <li className="flex items-start gap-2"><span className="text-secondary font-bold shrink-0">4.</span> Registra le evidenze grezze</li>
                <li className="flex items-start gap-2"><span className="text-secondary font-bold shrink-0">5.</span> Prendi una decisione basata sui dati</li>
              </ul>
              <button
                type="button"
                onClick={() => navigate("/v2/sprint", { replace: true })}
                className="w-full inline-flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 text-sm font-semibold transition"
              >
                Inizia il tuo Sprint →
              </button>
            </div>
          )}
          {(status === "failed" || status === "timeout") && (
            <div className="space-y-3 text-sm text-slate-300">
              <p>
                {status === "failed"
                  ? "Il pagamento non è andato a buon fine."
                  : "Non abbiamo ricevuto conferma in tempo utile. Se hai pagato, lo Sprint sarà disponibile a breve."}
              </p>
              <Link
                to="/v2/pricing"
                className="inline-flex items-center justify-center rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-2 text-sm font-semibold"
              >
                Torna a Pricing
              </Link>
            </div>
          )}
        </GlassCard>
      </div>
    </FunnelLayout>
  );
}
